import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowUpRight, 
  BriefcaseBusiness, 
  Building2, 
  ChevronRight, 
  Clock, 
  Globe, 
  HandHeart, 
  Heart, 
  Laugh, 
  MapPin, 
  Mic2, 
  Rocket, 
  Search, 
  Shield, 
  Star, 
  ThumbsUp, 
  Users, 
  Zap 
} from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const DEPARTMENTS = [
  "All Departments",
  "Engineering",
  "Design",
  "Product",
  "Marketing",
  "Sales",
  "Customer Success",
  "Finance",
  "Human Resources",
  "Operations"
];

const LOCATIONS = [
  "All Locations",
  "Remote",
  "New York",
  "San Francisco",
  "London",
  "Berlin",
  "Tokyo",
  "Singapore",
  "Sydney",
  "Toronto"
];

interface JobPosition {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  featured: boolean;
  salary: string;
  postedDate: string;
}

const JOB_POSITIONS: JobPosition[] = [
  {
    id: 1,
    title: "Senior Frontend Engineer",
    department: "Engineering",
    location: "Remote",
    type: "Full-time",
    featured: true,
    salary: "$120K - $150K",
    postedDate: "2023-09-15"
  },
  {
    id: 2,
    title: "Product Designer",
    department: "Design",
    location: "New York",
    type: "Full-time",
    featured: true,
    salary: "$90K - $120K",
    postedDate: "2023-09-18"
  },
  {
    id: 3,
    title: "Growth Marketing Manager",
    department: "Marketing",
    location: "San Francisco",
    type: "Full-time",
    featured: false,
    salary: "$100K - $130K",
    postedDate: "2023-09-10"
  },
  {
    id: 4,
    title: "Backend Developer",
    department: "Engineering",
    location: "Berlin",
    type: "Full-time",
    featured: false,
    salary: "$90K - $115K",
    postedDate: "2023-09-05"
  },
  {
    id: 5,
    title: "UX Researcher",
    department: "Design",
    location: "Remote",
    type: "Contract",
    featured: false,
    salary: "$80K - $100K",
    postedDate: "2023-09-20"
  },
  {
    id: 6,
    title: "DevOps Engineer",
    department: "Engineering",
    location: "London",
    type: "Full-time",
    featured: true,
    salary: "$110K - $140K",
    postedDate: "2023-09-12"
  },
  {
    id: 7,
    title: "Sales Director",
    department: "Sales",
    location: "New York",
    type: "Full-time",
    featured: false,
    salary: "$150K - $180K",
    postedDate: "2023-09-08"
  },
  {
    id: 8,
    title: "Customer Success Manager",
    department: "Customer Success",
    location: "Remote",
    type: "Full-time",
    featured: false,
    salary: "$85K - $105K",
    postedDate: "2023-09-14"
  }
];

const VALUES = [
  {
    icon: <Shield className="h-6 w-6 text-purple-500" />,
    title: "Trust & Security",
    description: "We build trust through transparency and prioritize security in everything we do"
  },
  {
    icon: <Rocket className="h-6 w-6 text-blue-500" />,
    title: "Innovation",
    description: "We embrace change and constantly seek better solutions to complex problems"
  },
  {
    icon: <Users className="h-6 w-6 text-green-500" />,
    title: "Collaboration",
    description: "We believe the best work happens when diverse minds come together"
  },
  {
    icon: <ThumbsUp className="h-6 w-6 text-amber-500" />,
    title: "Excellence",
    description: "We strive for excellence in our work and hold ourselves to high standards"
  },
  {
    icon: <Heart className="h-6 w-6 text-rose-500" />,
    title: "Empathy",
    description: "We approach challenges with compassion and understanding"
  },
  {
    icon: <Laugh className="h-6 w-6 text-cyan-500" />,
    title: "Joy",
    description: "We believe work should be fulfilling and fun"
  }
];

const BENEFITS = [
  {
    icon: <Globe className="h-6 w-6 text-blue-600" />,
    title: "Remote-First",
    description: "Work from anywhere in the world with our distributed team approach"
  },
  {
    icon: <Clock className="h-6 w-6 text-green-600" />,
    title: "Flexible Hours",
    description: "Set your own schedule and work when you're most productive"
  },
  {
    icon: <BriefcaseBusiness className="h-6 w-6 text-amber-600" />,
    title: "Career Growth",
    description: "Clear paths for advancement with mentorship and development opportunities"
  },
  {
    icon: <HandHeart className="h-6 w-6 text-purple-600" />,
    title: "Comprehensive Benefits",
    description: "Health, dental, vision insurance with 100% premium coverage"
  },
  {
    icon: <Zap className="h-6 w-6 text-cyan-600" />,
    title: "Learning Budget",
    description: "$2,500 annual budget for courses, books, and conferences"
  },
  {
    icon: <Mic2 className="h-6 w-6 text-rose-600" />,
    title: "Open Communication",
    description: "Regular town halls and transparent decision-making processes"
  }
];

const TESTIMONIALS = [
  {
    quote: "Joining DGK was the best career move I've ever made. The culture of innovation and support has helped me grow tremendously as an engineer.",
    author: "Sarah Chen",
    role: "Senior Frontend Engineer",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg",
    years: "3 years at DGK"
  },
  {
    quote: "I love the autonomy and trust. Here, you're empowered to make decisions and take ownership of your work in a way I haven't experienced elsewhere.",
    author: "Marcus Johnson",
    role: "Product Manager",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    years: "2 years at DGK"
  },
  {
    quote: "The remote-first approach allows me to balance work with my family life. I'm more productive and happier than I've ever been in my career.",
    author: "Aisha Patel",
    role: "UX Designer",
    avatar: "https://randomuser.me/api/portraits/women/66.jpg",
    years: "1.5 years at DGK"
  }
];

export default function Careers() {
  const [searchTerm, setSearchTerm] = useState("");
  const [department, setDepartment] = useState("All Departments");
  const [location, setLocation] = useState("All Locations");
  const [filteredJobs, setFilteredJobs] = useState<JobPosition[]>(JOB_POSITIONS);
  
  const [heroRef, heroInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [valuesRef, valuesInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [benefitsRef, benefitsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    let results = JOB_POSITIONS;
    
    if (searchTerm) {
      results = results.filter(job => 
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (department !== "All Departments") {
      results = results.filter(job => job.department === department);
    }
    
    if (location !== "All Locations") {
      results = results.filter(job => job.location === location);
    }
    
    setFilteredJobs(results);
  }, [searchTerm, department, location]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }).format(date);
  };

  const daysAgo = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Careers | DGK Freelance Hub</title>
        <meta name="description" content="Join our team at DGK Freelance Hub and help shape the future of freelancing" />
      </Helmet>
      
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative bg-gradient-to-br from-purple-800 via-indigo-800 to-blue-800 text-white py-20 overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[length:20px_20px]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-purple-900/50"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-[10%] w-20 h-20 bg-purple-500/20 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-[15%] w-32 h-32 bg-blue-500/20 rounded-full blur-xl"></div>
        <div className="absolute top-1/2 right-[20%] w-16 h-16 bg-indigo-500/30 rounded-full blur-lg"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Join Our Mission to 
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent"> Transform Freelancing</span>
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-3xl mx-auto">
              We're building the future of work. Join a team that's passionate about revolutionizing
              the freelance economy through innovation and technology.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
              <Button size="lg" className="bg-white text-purple-800 hover:bg-purple-50">
                Explore Open Positions <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Our Values & Culture
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-16 bg-purple-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <h3 className="text-4xl font-bold text-purple-700">30+</h3>
              <p className="text-gray-600 mt-2">Open Positions</p>
            </div>
            <div className="text-center">
              <h3 className="text-4xl font-bold text-purple-700">12</h3>
              <p className="text-gray-600 mt-2">Countries</p>
            </div>
            <div className="text-center">
              <h3 className="text-4xl font-bold text-purple-700">85%</h3>
              <p className="text-gray-600 mt-2">Remote Workers</p>
            </div>
            <div className="text-center">
              <h3 className="text-4xl font-bold text-purple-700">4.8/5</h3>
              <p className="text-gray-600 mt-2">Employee Rating</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Jobs Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Open Positions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our talented team and help us revolutionize the freelance economy
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search positions..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <select
                className="border border-gray-300 rounded-md px-4 py-2"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              >
                {DEPARTMENTS.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
              
              <select
                className="border border-gray-300 rounded-md px-4 py-2"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              >
                {LOCATIONS.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-4">
              {filteredJobs.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-lg text-gray-500">No positions matching your criteria</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => {
                      setSearchTerm("");
                      setDepartment("All Departments");
                      setLocation("All Locations");
                    }}
                  >
                    Reset Filters
                  </Button>
                </div>
              ) : (
                filteredJobs.map((job) => (
                  <Card key={job.id} className="hover:shadow-md transition-all duration-300">
                    <div className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                            {job.featured && (
                              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200">
                                Featured
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-gray-500">
                            <span className="flex items-center">
                              <Building2 className="mr-1 h-4 w-4" />
                              {job.department}
                            </span>
                            <span className="flex items-center">
                              <MapPin className="mr-1 h-4 w-4" />
                              {job.location}
                            </span>
                            <span>{job.type}</span>
                          </div>
                        </div>
                        
                        <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end">
                          <div className="text-lg font-medium text-gray-900">{job.salary}</div>
                          <div className="text-sm text-gray-500">
                            Posted {daysAgo(job.postedDate)} days ago
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-3 mt-4">
                        <Button className="bg-purple-600 hover:bg-purple-700">
                          Apply Now <ArrowUpRight className="ml-1 h-4 w-4" />
                        </Button>
                        <Button variant="outline">
                          View Details
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <section ref={valuesRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core principles guide everything we do
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {VALUES.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300">
                  <div className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mb-4">
                      {value.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Work With Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer competitive benefits to help you thrive both personally and professionally
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {BENEFITS.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300 border-t-4 border-t-purple-500">
                  <div className="p-6">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Employee Stories</h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              Hear what our team members have to say about working at DGK
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm text-white border-none hover:bg-white/20 transition-all duration-300">
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                      <img 
                        src={testimonial.avatar} 
                        alt={testimonial.author} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-semibold">{testimonial.author}</div>
                      <div className="text-sm text-purple-200">{testimonial.role}</div>
                    </div>
                  </div>
                  
                  <p className="italic mb-6">"{testimonial.quote}"</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-purple-200">{testimonial.years}</div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Common questions about working at DGK
            </p>
          </div>
          
          <Tabs defaultValue="application">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="application">Application Process</TabsTrigger>
              <TabsTrigger value="working">Working at DGK</TabsTrigger>
              <TabsTrigger value="remote">Remote Work</TabsTrigger>
            </TabsList>
            <TabsContent value="application" className="mt-6">
              <div className="space-y-6">
                {[
                  {
                    question: "What does the application process look like?",
                    answer: "Our process typically includes an initial application review, a screening call with HR, a technical/skills assessment, interviews with the team, and a final decision. The entire process usually takes 2-3 weeks."
                  },
                  {
                    question: "Do you provide feedback to unsuccessful candidates?",
                    answer: "Yes, we strive to provide constructive feedback to all candidates who reach the interview stage, as we believe in helping everyone grow professionally."
                  },
                  {
                    question: "Can I apply for multiple positions?",
                    answer: "Yes, you can apply for up to three positions that match your skills and experience. We'll consider you for the role that best fits your profile."
                  }
                ].map((item, index) => (
                  <div key={index} className="pb-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.question}</h3>
                    <p className="text-gray-600">{item.answer}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="working" className="mt-6">
              <div className="space-y-6">
                {[
                  {
                    question: "What is the company culture like?",
                    answer: "Our culture emphasizes collaboration, innovation, and work-life balance. We value diverse perspectives and encourage open communication at all levels of the organization."
                  },
                  {
                    question: "What opportunities are there for professional growth?",
                    answer: "We offer regular learning and development opportunities, mentorship programs, and clear career progression paths. We also provide a learning budget for conferences, courses, and certifications."
                  },
                  {
                    question: "How do you support diversity and inclusion?",
                    answer: "We have active D&I initiatives including employee resource groups, unconscious bias training, and inclusive hiring practices. We regularly review our policies to ensure an equitable workplace."
                  }
                ].map((item, index) => (
                  <div key={index} className="pb-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.question}</h3>
                    <p className="text-gray-600">{item.answer}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="remote" className="mt-6">
              <div className="space-y-6">
                {[
                  {
                    question: "Do you support fully remote work?",
                    answer: "Yes, we're a remote-first company with team members across multiple time zones. We believe in hiring the best talent regardless of location."
                  },
                  {
                    question: "How do you maintain team cohesion with remote workers?",
                    answer: "We use a mix of virtual team-building activities, regular video meetings, collaborative tools, and occasional in-person retreats to foster strong connections among team members."
                  },
                  {
                    question: "What equipment do you provide for remote workers?",
                    answer: "We provide a home office stipend for purchasing necessary equipment, including a laptop, monitor, ergonomic chair, and other essentials to create a comfortable and productive workspace."
                  }
                ].map((item, index) => (
                  <div key={index} className="pb-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.question}</h3>
                    <p className="text-gray-600">{item.answer}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Join Our Team?</h2>
          <p className="text-xl text-purple-100 max-w-3xl mx-auto mb-8">
            Explore our open positions and take the next step in your career journey
          </p>
          <Button size="lg" className="bg-white text-purple-700 hover:bg-purple-50">
            View All Positions <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
